import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useCallback, Fragment } from 'react';
import './table.scss';
import _ from 'lodash'
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import cx from 'classnames';

import { Table, TableBody, TableRow, TableCell, TableContainer, Checkbox, Grid, Icon } from '@mui/material';

import { LinearProgress, Button, DragDropList, Checkbox as CustomCheckbox, MenuComponent } from '../index.js';
import Pagination from '../Pagination/index.js';
import TableHeader from './.components/header.js';
import AreaMenu, { Area, Menu } from '../AreaMenu/index.js';
import { Waypoint } from 'react-waypoint';

const condensationHeigth = 7

const TableView = forwardRef((props, ref) => {

    const {
        keyColumn,
        columns = [],
        topheight,
        data = [],
        grouping,
        groupingValueToCompare,
        groupingArea,
        searchText,
        onSelectedItems,
        hidePagination,
        exportOrientation,
        exportName,
        onChangeColumns,
        hideAllInPagination,
        fixHeader = true,
        infiniteScroll = false,
        nextToken,
        height,
        disableItemSelection = false,
        lockedColumns = false,
        onChangeSort,
        defaultDensity,
        valuesPerPage,
        menuOptions,
        onClickMenu,
        messages,
        customColumn,
    } = props

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(infiniteScroll ? 25 : 10);
    const [sort, setSort] = useState({ key: 'nametag', order: 'desc' });
    const [selectedItems, setSelectedItems] = useState([]);
    const [density, setDensity] = useState(0);

    useEffect(() => {
        if (props.data && props.data.length > 0 && page == 0) {
            setPage(props.defaultPage || 1)
            setDensity(defaultDensity || 0);
            props.defaultSort && setSort(props.defaultSort)
            props.defaultRowsPerPage && setRowsPerPage(props.defaultRowsPerPage);
            if (props.onChangeDisplayData) props.onChangeDisplayData(filterData)
        }
    }, [props.data])

    let currentGroup
    let sortGroup
    if (grouping)
        sortGroup = { key: groupingValueToCompare, order: 'asc' }

    useImperativeHandle(ref, () => ({
        selectedItems: selectedItems,
        exportPDF: exportPDF,
        exportCSV: exportCSV,
        setPage: setPage,
        setSelectedItems: setSelectedItems,
        onChangeDensity: onChangeDensity,
        density: density,
        setRowsPerPage: setRowsPerPage,
        setSort: setSort,
    }))

    const [filterData, total] = useMemo(() => {
        if (infiniteScroll && nextToken === undefined) {
            return [data, props.total];
        }

        if (infiniteScroll) {
            return [data, 0];
        }

        if (props.onChangePage) {
            return [data, props.total];
        }

        let sorts = [];
        let viewData = data
        let total = data.length


        if (sortGroup) sorts.push(sortGroup)
        if (sort) sorts.push(sort)

        if (searchText && typeof searchText !== 'undefined' && searchText.length > 0) {
            viewData = data.filter(x => {
                let result = false;
                columns.forEach(col => {
                    if (getClearValue(x[col.key]).toString().toLowerCase().indexOf(searchText.toLowerCase()) >= 0)
                        result = true;
                });

                return result
            })

            total = viewData.length
        }

        if (sorts && sorts.length > 0)
            viewData = multipleSort(viewData || [], sorts || [])


        return [viewData, total];
    });

    const filterDataPaginate = useMemo(() => {
        if (infiniteScroll || props.onChangePage) {
            return filterData;
        }

        let currentPage = page - 1;
        if (!hidePagination && rowsPerPage != 'ALL') {
            let valuesPaginate = filterData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
            return valuesPaginate;
        }

        return filterData;
    });

    const exportCSV = useCallback(() => {
        const headers = columns.filter(x => x.export && x.export)
        let csvContent = `${headers.map(x => x.title).join(',')}` + '\n';


        let rows = filterData;
        if (selectedItems?.length) rows = selectedItems;

        rows.forEach((row, index) => {
            //its necesary format the text because if possible has a line breaks or ,

            let itemValues = headers.map((col) => {
                let value = col.exportValue && col.exportValue(row) || row[col.key] || "";
                value = value.toString().replaceAll(/(\r\n|\n|\r)/gm, "") || ""
                if (value.includes(","))
                    value = `"${value}"`;

                return value
            })


            let dataString = itemValues.join(',');
            csvContent += index < rows.length ? dataString + '\n' : dataString;
        });

        let fileName = `${exportName || 'exported-data'}.csv`;
        let mimeType = 'text/csv;encoding:utf-8;charset=UTF-8';
        let a = document.createElement('a');

        mimeType = mimeType || 'application/octet-stream';

        if (navigator.msSaveBlob) {
            // IE10
            navigator.msSaveBlob(
                new Blob([csvContent], {
                    type: mimeType,
                }),
                fileName,
            );
        } else if (URL && 'download' in a) {
            //html5 A[download]
            a.href = URL.createObjectURL(
                new Blob([csvContent], {
                    type: mimeType,
                }),
            );
            a.setAttribute('download', fileName);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            location.href = 'data:application/octet-stream,' + encodeURIComponent(csvContent); // only this mime type is supported
        }
    });

    const exportPDF = useCallback(() => {
        const unit = 'pt';
        const size = 'A4'; // Use A1, A2, A3 or A4
        const orientation = exportOrientation || 'portrait'; // portrait or landscape

        const doc = new jsPDF(orientation, unit, size);
        doc.setFontSize(12);

        jsPDF.autoTableSetDefaults({ headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] } }, doc)

        let rows = filterData;
        if (selectedItems?.length) rows = selectedItems;

        const headers = columns.filter(x => x.show && x.export);
        let content = {
            pageBreak: 'auto',
            startY: 40,
            head: [headers.map(x => x.title)],
            //its necesary loop the data to assign styles for wrap text when is a long value
            body: rows.map((row) => {
                return headers.map((col) => {
                    return { content: col.exportValue && col.exportValue(row) || row[col.key] || "", styles: { cellWidth: 'auto', overflow: 'linebreak', minCellWidth: 50 } }
                })
            }),
        };

        doc.autoTable(content);
        doc.save(`${exportName || 'export'}.pdf`);
    });

    const onChangeDensity = (den) => {
        let value = den ? den : density === 3 ? 1 : density + 1;
        setDensity(value);
        props.onChangeDensity && props.onChangeDensity(value);
    }

    const lockedColumnsHandler = useCallback((key, columns, disable, id) => {
        let left = !disable ? 43 : 0;
        columns.filter((x, index) => x?.locked && index < key).map((col) => {
            left += document.getElementById(`${id}-${col?.key}`)?.offsetWidth || col?.width || 0
        })
        left = menuOptions ? left + 43 : left
        left = customColumn ? left + 43 : left
        return left;
    });

    return (
        <div style={{ height: (height ? height : (topheight && `calc(100vh - ${topheight}px)` || '100%')), width: '100%' }}>
            <Grid item className="table_component">
                <Fragment>
                    <div className="container-loading">
                        {
                            props.loading && <LinearProgress color="primary" />
                        }
                    </div>
                    <TableContainer>
                        <Table
                            aria-labelledby="tableTitle"
                            size={'medium'}
                            aria-label="enhanced table"
                            stickyHeader={fixHeader}
                        >
                            <TableHeader
                                {...props}
                                columnsView={columns?.filter(x => x.show)}
                                onChangeColumns={(items) => {
                                    let newColumns = [...columns]
                                    items.forEach((col, index) => {
                                        let indexCol = newColumns.findIndex(x => x.key == col.key)
                                        let newCol = { ...newColumns[indexCol], ...col }
                                        newColumns.splice(indexCol, 1)
                                        newColumns.splice(index, 0, newCol)
                                    })
                                    onChangeColumns(newColumns);
                                }
                                }
                                sort={sort}
                                page={page}
                                dataItems={filterDataPaginate}
                                onChangeSort={(newSort) => {
                                    setSort(newSort);
                                    onChangeSort && onChangeSort(newSort);
                                }}
                                selectedItems={selectedItems}
                                onChageSelectedItems={(newSelectedtItems) => {
                                    setSelectedItems(newSelectedtItems)
                                    onSelectedItems && onSelectedItems(newSelectedtItems)
                                }}
                                lockedColumns={lockedColumns}
                                lockedColumnsHandler={lockedColumnsHandler}
                            />
                            <TableBody>
                                {
                                    filterDataPaginate.map((row, index) => {
                                        let printGroup = false
                                        if (grouping) {
                                            let newGroup = row[groupingValueToCompare]
                                            printGroup = !_.isEqual(newGroup, currentGroup)
                                            currentGroup = printGroup ? newGroup : currentGroup
                                        }
                                        return (
                                            <Fragment key={index}>
                                                {
                                                    printGroup &&
                                                    <Fragment>
                                                        <TableRow>
                                                            <TableCell colSpan={columns.filter(x => x.show).length + 1} style={{ padding: 0 }}>
                                                                {
                                                                    groupingArea
                                                                        ? groupingArea(currentGroup, row)
                                                                        : currentGroup.toString()
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    </Fragment>
                                                }

                                                <TableRow>
                                                    {
                                                        !disableItemSelection &&
                                                        <TableCell padding="checkbox" className={cx(lockedColumns && "locked-columns")}>
                                                            <Checkbox
                                                                style={{ color: "black", zIndex: 0 }}
                                                                checked={selectedItems.findIndex(x => keyColumn && x[keyColumn] == row[keyColumn] || x == row) > -1}
                                                                onClick={() => {
                                                                    let newSelectedItems = [...selectedItems];
                                                                    let index = newSelectedItems.findIndex(x => keyColumn && x[keyColumn] == row[keyColumn] || x == row)
                                                                    if (index > -1)
                                                                        newSelectedItems.splice(index, 1)
                                                                    else
                                                                        newSelectedItems.push(row)

                                                                    setSelectedItems(newSelectedItems)
                                                                    onSelectedItems && onSelectedItems(newSelectedItems)
                                                                }}
                                                                inputProps={{ 'aria-label': 'select all desserts' }}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    }
                                                    {
                                                        menuOptions &&
                                                        <TableCell
                                                            padding="checkbox"
                                                            className={cx("context-menu-container", lockedColumns && "locked-columns")}
                                                        >
                                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                                                                <MenuComponent
                                                                    options={menuOptions(row)}
                                                                    icon="more_vert"
                                                                    className="context-menu-table"
                                                                    onClick={(type, value, clickEvent) => {
                                                                        onClickMenu && onClickMenu(type, value, clickEvent);
                                                                    }}
                                                                    value={row}
                                                                />
                                                            </div>
                                                        </TableCell>
                                                    }
                                                    {
                                                        customColumn &&
                                                        <TableCell
                                                            padding="checkbox"
                                                            className={cx("context-menu-container", lockedColumns && "locked-columns")}
                                                        >
                                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                                                                {customColumn?.render ? customColumn.render(row) : <></>}
                                                            </div>
                                                        </TableCell>
                                                    }
                                                    {
                                                        columns.filter(x => x.show).map((col, index) => {
                                                            return (
                                                                <TableCell key={col.key}
                                                                    className={cx(lockedColumns && col?.locked && "locked-columns")}
                                                                    style={{
                                                                        left: lockedColumns && col?.locked && lockedColumnsHandler(index, columns.filter(x => x?.show) || [], disableItemSelection, "cell"),
                                                                        paddingTop: defaultDensity && ((density - 1) * condensationHeigth),
                                                                        paddingBottom: defaultDensity && ((density - 1) * condensationHeigth)
                                                                    }}
                                                                    id={`cell-${col.key}`}
                                                                >
                                                                    {
                                                                        col.render
                                                                            ? col.render(row)
                                                                            : row[col.key]?.toString()
                                                                    }
                                                                </TableCell>
                                                            )
                                                        })
                                                    }
                                                </TableRow>
                                            </Fragment>
                                        )
                                    })
                                }

                                {
                                    infiniteScroll &&
                                    <TableRow>
                                        <TableCell style={{ textAlign: 'center', padding: 10, padding: 0 }} colSpan={columns.filter(x => x.show).length + 1}>
                                            <Waypoint
                                                onEnter={() => {
                                                    if (nextToken) {
                                                        if (props.onChangePage) props.onChangePage();
                                                    } else if (nextToken === undefined && total) {
                                                        let newPage = page + 1;
                                                        if (filterDataPaginate.length < total) {
                                                            setPage(newPage);
                                                            if (props.onChangePage) props.onChangePage(newPage, rowsPerPage);
                                                            if (props.onChangeDisplayData) props.onChangeDisplayData(filterData);
                                                        }
                                                    }
                                                }}
                                            />

                                            {
                                                (!props.moreRowsAvailable && !props.loading) &&
                                                <div className="no-more-items">
                                                    {props.noMoreItemsMessage || 'There are no more items'}
                                                </div>
                                            }
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Fragment>
            </Grid>

            {
                (!hidePagination && !infiniteScroll) &&
                <Grid item xs>
                    <div
                        style={{
                            padding: '5px 0px 0px 0px'
                        }}
                    >
                        <Pagination
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={total}
                            pagination
                            hideAllInPagination={hideAllInPagination}
                            onChangePage={(event, value) => {
                                setPage(value);
                                if (props.onChangePage) props.onChangePage(value);
                                if (props.onChangeDisplayData) props.onChangeDisplayData(filterData);
                                setSelectedItems([]);
                                onSelectedItems && onSelectedItems([])
                            }}
                            onChangeRowsPerPage={(value) => {
                                setRowsPerPage(value);
                                setPage(1);
                                if (props.onChangeRowsPerPage) props.onChangeRowsPerPage(value);
                                if (props.onChangeDisplayData) props.onChangeDisplayData(filterData);
                                setSelectedItems([]);
                                onSelectedItems && onSelectedItems([])
                            }}
                            valuesPerPage={valuesPerPage}
                        />
                    </div>
                </Grid>
            }
        </div>
    )
})

export default TableView

export const TableButtons = (props) => {
    const {
        messages,
        tableRef,
        columns,
        onChangeColumns,
        buttons = [
            'export',
            'orderColumn'
        ],
        lockedColumns = false,
        externalExport,
        noCSV,
        noPDF,
    } = props;

    const availableButtons = {
        export: (key) => {
            return (
                <AreaMenu key={key} id="exportTable" clickClose>
                    <Area>
                        <Button
                            type="icon"
                            icon="get_app"
                            tooltip={messages['export']}
                            onClick={() => { }}
                        />
                    </Area>
                    <Menu className="menu-columns">
                        <ul >
                            {!noCSV && <li onClick={() => { externalExport ? externalExport('CSV') : tableRef.current.exportCSV() }}>{messages ? messages['exportCSV'] : 'Export CSV'}</li>}
                            {!noPDF && <li onClick={() => { externalExport ? externalExport('PDF') : tableRef.current.exportPDF() }}>{messages ? messages['exportPDF'] : 'Export PDF'}</li>}
                        </ul>
                    </Menu>
                </AreaMenu>
            )
        },
        orderColumn: (key) => {
            return (
                <AreaMenu key={key} id="columnsOrder">
                    <Area>
                        <Button
                            type="icon"
                            icon="view_week"
                            tooltip={messages['changeColumnOrder']}
                            onClick={() => { }}
                        />
                    </Area>
                    <Menu style={{ width: 300 }}>
                        <div>
                            <div className="options-menu">
                                <Grid container spacing={2}>
                                    <Grid item xs={lockedColumns ? 8 : 10} style={{ display: 'flex', alignItems: 'center' }}>
                                        <span className="options-menu-title">Column</span>
                                    </Grid>
                                    <Grid item xs={2} className="options-menu-icons">
                                        <Icon>visibility</Icon>
                                    </Grid>
                                    {lockedColumns &&
                                        <Grid item xs={2} className="options-menu-icons">
                                            <Icon>lock</Icon>
                                        </Grid>
                                    }
                                </Grid>
                                <hr className="options-menu-separator" />
                            </div>
                            <DragDropList
                                items={columns}
                                keyName="key"
                                itemRenderer={(item) => {
                                    if (!item.icon) {
                                        return (
                                            <Grid container spacing={2}>
                                                <Grid item xs={lockedColumns ? 8 : 10} style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span>{item.title}</span>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <CustomCheckbox
                                                        id={item.key}
                                                        style={{ color: "black" }}
                                                        label={""}
                                                        value={item.show}
                                                        onChange={(checked) => {
                                                            let index = columns.findIndex(x => x == item)
                                                            let newColumns = [...columns]
                                                            newColumns[index].show = checked

                                                            onChangeColumns(newColumns, checked)
                                                        }}
                                                        inputProps={{ 'aria-label': 'select all desserts' }}
                                                    />
                                                </Grid>
                                                {lockedColumns &&
                                                    <Grid item xs={2}>
                                                        <CustomCheckbox
                                                            id={item.key}
                                                            style={{ color: "black" }}
                                                            label={""}
                                                            value={item.locked}
                                                            onChange={(checked) => {
                                                                let index = columns.findIndex(x => x == item)
                                                                let newColumns = [...columns]
                                                                newColumns[index].locked = checked

                                                                onChangeColumns(newColumns)
                                                            }}
                                                            inputProps={{ 'aria-label': 'select all desserts' }}
                                                        />
                                                    </Grid>
                                                }
                                            </Grid>
                                        );
                                    } else return <></>
                                }}
                                onDragEnd={(result) => {

                                    if (!result.destination) return;

                                    const newColumns = [...columns];

                                    const [reorderedItem] = newColumns.splice(result.source.index, 1);
                                    newColumns.splice(result.destination.index, 0, reorderedItem);

                                    onChangeColumns(newColumns);
                                }}
                            />
                        </div>
                    </Menu>
                </AreaMenu>
            );
        }
    };

    return (
        <div style={{ display: 'flex' }}>
            {
                buttons.map((button, key) => {
                    return availableButtons[button](key);
                })
            }
        </div>
    )
}

const getClearValue = (value) => {
    if (!value) return ""

    if (typeof value == 'undefined') return ""

    return value
}

const multipleSort = (data, sorts) => {

    return [...data].sort((a, b) => {
        let i = 0, result = 0;
        while (i < sorts.length && result === 0) {
            result = (sorts[i].order == 'asc' ? 1 : -1) * (getClearValue(a[sorts[i].key]).toString() < getClearValue(b[sorts[i].key]).toString() ? -1 : (getClearValue(a[sorts[i].key]).toString() > getClearValue(b[sorts[i].key]).toString() ? 1 : 0));
            i++;
        }
        return result;
    })

}
